import { useCallback, useEffect, useMemo, useState } from "react";

import {
  createBureauMember,
  deleteBureauMember,
  fetchBureauMembers,
  updateBureauMember,
  uploadAdminMedia
} from "@/features/admin/api";
import { useAdminSession } from "@/features/admin/useAdminSession";
import type { AdminBureauMember } from "@/features/admin/types";

import PaginationControls from "./PaginationControls";

const DEFAULT_AVATAR =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 120 120'><rect width='120' height='120' rx='24' fill='%23E8521A'/><circle cx='60' cy='46' r='22' fill='white' fill-opacity='0.92'/><path d='M24 102c8-20 25-30 36-30s28 10 36 30' fill='white' fill-opacity='0.92'/></svg>";

type FormState = {
  nom: string;
  role: string;
  departement: string;
  email: string;
  telephone: string;
  ordre: number;
};

const EMPTY_FORM: FormState = {
  nom: "",
  role: "",
  departement: "",
  email: "",
  telephone: "",
  ordre: 0
};
const BUREAU_MEMBERS_PER_PAGE = 4;

export default function BureauTab() {
  const { request } = useAdminSession();
  const [members, setMembers] = useState<AdminBureauMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" } | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deletingMemberId, setDeletingMemberId] = useState<string | null>(null);
  const [editingMember, setEditingMember] = useState<AdminBureauMember | null>(null);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [imageInputKey, setImageInputKey] = useState(0);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [currentPage, setCurrentPage] = useState(1);

  const loadMembers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const nextMembers = await fetchBureauMembers(request);
      setMembers(nextMembers);
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Impossible de charger le bureau";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    void loadMembers();
  }, [loadMembers]);

  useEffect(() => {
    if (!selectedImageFile) {
      return;
    }

    const preview = URL.createObjectURL(selectedImageFile);
    setImagePreviewUrl(preview);

    return () => URL.revokeObjectURL(preview);
  }, [selectedImageFile]);

  const showToast = (msg: string, type: "success" | "error") => {
    setToast({ msg, type });
    window.setTimeout(() => setToast(null), 3000);
  };

  const openNew = () => {
    setEditingMember(null);
    setForm({
      ...EMPTY_FORM,
      ordre: members.length + 1
    });
    setSelectedImageFile(null);
    setImagePreviewUrl(null);
    setImageInputKey((current) => current + 1);
    setShowForm(true);
  };

  const openEdit = (member: AdminBureauMember) => {
    setEditingMember(member);
    setForm({
      nom: member.nom,
      role: member.role,
      departement: member.departement ?? "",
      email: member.email ?? "",
      telephone: member.telephone ?? "",
      ordre: member.ordre
    });
    setSelectedImageFile(null);
    setImagePreviewUrl(member.avatar === DEFAULT_AVATAR ? null : member.avatar);
    setImageInputKey((current) => current + 1);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.nom.trim() || !form.role.trim()) {
      setError("Le nom et le rôle sont obligatoires.");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      let resolvedAvatar = editingMember?.avatar === DEFAULT_AVATAR ? "" : editingMember?.avatar ?? "";

      if (selectedImageFile) {
        const [asset] = await uploadAdminMedia(request, [selectedImageFile], "asc-sonatel/bureau");
        resolvedAvatar = asset?.url ?? resolvedAvatar;
      }

      const payload = {
        nom: form.nom.trim(),
        role: form.role.trim(),
        departement: form.departement.trim() || null,
        email: form.email.trim() || null,
        telephone: form.telephone.trim() || null,
        ordre: Number(form.ordre) || 0,
        avatar: resolvedAvatar || null
      };

      if (editingMember) {
        await updateBureauMember(request, editingMember.id, payload);
        showToast("Membre du bureau mis à jour.", "success");
      } else {
        await createBureauMember(request, payload);
        showToast("Membre du bureau ajouté.", "success");
      }

      setShowForm(false);
      setSelectedImageFile(null);
      setImagePreviewUrl(null);
      setImageInputKey((current) => current + 1);
      await loadMembers();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Enregistrement impossible";
      setError(message);
      showToast(message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (member: AdminBureauMember) => {
    setDeletingMemberId(member.id);
    setError(null);

    try {
      await deleteBureauMember(request, member.id);
      showToast(`Le profil de ${member.nom} a été supprimé du bureau.`, "success");
      await loadMembers();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Suppression impossible";
      setError(message);
      showToast(message, "error");
    } finally {
      setDeletingMemberId(null);
    }
  };

  const orderedMembers = useMemo(
    () => [...members].sort((first, second) => first.ordre - second.ordre || first.nom.localeCompare(second.nom)),
    [members]
  );
  const totalPages = Math.max(1, Math.ceil(orderedMembers.length / BUREAU_MEMBERS_PER_PAGE));
  const paginatedMembers = useMemo(() => {
    const startIndex = (currentPage - 1) * BUREAU_MEMBERS_PER_PAGE;
    return orderedMembers.slice(startIndex, startIndex + BUREAU_MEMBERS_PER_PAGE);
  }, [currentPage, orderedMembers]);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  return (
    <div className="space-y-6 relative">
      {toast && (
        <div className={`fixed top-6 right-6 z-50 px-5 py-3 rounded-xl shadow-lg text-white text-sm font-medium flex items-center gap-2 ${toast.type === "success" ? "bg-emerald-500" : "bg-red-500"}`}>
          <i className={`${toast.type === "success" ? "ri-checkbox-circle-line" : "ri-close-circle-line"} text-lg`}></i>
          {toast.msg}
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-[#1A1A1A]">Gestion du Bureau</h1>
          <p className="text-gray-500 text-sm mt-1">Ajoute, modifie, supprime les membres du bureau et leurs photos.</p>
          {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => void loadMembers()}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white text-gray-600 text-sm font-medium border border-gray-200 hover:border-[#E8521A] hover:text-[#E8521A] transition-colors whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i>
            Actualiser
          </button>
          <button
            onClick={openNew}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#E8521A] text-white text-sm font-medium hover:bg-[#d44a16] transition-colors whitespace-nowrap"
          >
            <i className="ri-user-add-line"></i>
            Ajouter un membre
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="text-2xl font-bold text-[#E8521A]">{members.length}</div>
          <div className="text-sm text-gray-500 mt-1">Membres du bureau</div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="text-2xl font-bold text-[#1A1A1A]">{members.filter((member) => member.avatar && member.avatar !== DEFAULT_AVATAR).length}</div>
          <div className="text-sm text-gray-500 mt-1">Photos définies</div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <div className="text-2xl font-bold text-[#1A8C7A]">{new Set(members.map((member) => member.departement).filter(Boolean)).size}</div>
          <div className="text-sm text-gray-500 mt-1">Services représentés</div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {loading ? (
          Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="h-48 rounded-2xl border border-gray-100 bg-white animate-pulse"></div>
          ))
        ) : paginatedMembers.map((member) => (
          <div key={member.id} className="bg-white rounded-2xl border border-gray-100 p-5 hover:border-orange-200 transition-colors">
            <div className="flex items-start gap-4">
              <img src={member.avatar ?? DEFAULT_AVATAR} alt={member.nom} className="w-16 h-16 rounded-full object-cover flex-shrink-0 border border-orange-100" />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-base font-bold text-[#1A1A1A]">{member.nom}</h3>
                    <p className="text-sm text-[#E8521A] mt-1">{member.role}</p>
                  </div>
                  <span className="inline-flex px-2.5 py-1 rounded-full bg-gray-100 text-gray-500 text-xs font-semibold">
                    Ordre {member.ordre}
                  </span>
                </div>

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-500">
                    <i className="ri-building-line text-[#E8521A]"></i>
                    {member.departement ?? "Service non renseigné"}
                  </div>
                  <div className="flex items-center gap-2 text-gray-500">
                    <i className="ri-mail-line text-[#E8521A]"></i>
                    {member.email ?? "Email non renseigné"}
                  </div>
                  <div className="flex items-center gap-2 text-gray-500 sm:col-span-2">
                    <i className="ri-phone-line text-[#E8521A]"></i>
                    {member.telephone ?? "Téléphone non renseigné"}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => openEdit(member)}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-orange-50 text-[#E8521A] text-sm font-medium hover:bg-orange-100 transition-colors"
              >
                <i className="ri-edit-line"></i>
                Modifier
              </button>
              <button
                onClick={() => void handleDelete(member)}
                disabled={deletingMemberId === member.id}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-red-50 text-red-500 text-sm font-medium hover:bg-red-100 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
              >
                <i className={`${deletingMemberId === member.id ? "ri-loader-4-line animate-spin" : "ri-delete-bin-line"}`}></i>
                Supprimer
              </button>
            </div>
          </div>
        ))}
      </div>

      {!loading && orderedMembers.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-gray-200 px-6 py-14 text-center">
          <i className="ri-team-line text-4xl text-gray-300"></i>
          <p className="mt-4 text-sm font-medium text-gray-600">Aucun membre du bureau enregistré pour le moment.</p>
        </div>
      ) : null}

      {!loading && orderedMembers.length > 0 ? (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemLabel="membres du bureau"
        />
      ) : null}

      {showForm ? (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div
            className="w-full max-w-3xl rounded-[28px] bg-white border border-gray-100 shadow-[0_30px_100px_rgba(0,0,0,0.16)] overflow-hidden"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-[#1A1A1A]">{editingMember ? "Modifier le membre du bureau" : "Ajouter un membre du bureau"}</h3>
                <p className="text-sm text-gray-500 mt-1">Les changements seront visibles automatiquement sur l’accueil et la page club.</p>
              </div>
              <button
                onClick={() => setShowForm(false)}
                className="w-9 h-9 rounded-xl hover:bg-gray-100 transition-colors flex items-center justify-center text-gray-500"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="p-6 space-y-5 max-h-[80vh] overflow-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Nom complet</label>
                  <input
                    type="text"
                    value={form.nom}
                    onChange={(event) => setForm((current) => ({ ...current, nom: event.target.value }))}
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
                    placeholder="Nom du membre"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Rôle / Fonction</label>
                  <input
                    type="text"
                    value={form.role}
                    onChange={(event) => setForm((current) => ({ ...current, role: event.target.value }))}
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
                    placeholder="Président, secrétaire..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Service / Direction</label>
                  <input
                    type="text"
                    value={form.departement}
                    onChange={(event) => setForm((current) => ({ ...current, departement: event.target.value }))}
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
                    placeholder="Ex: DRH, DSI, DV"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Ordre d’affichage</label>
                  <input
                    type="number"
                    min={0}
                    value={form.ordre}
                    onChange={(event) => setForm((current) => ({ ...current, ordre: Number(event.target.value) }))}
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
                    placeholder="Optionnel"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Téléphone</label>
                  <input
                    type="text"
                    value={form.telephone}
                    onChange={(event) => setForm((current) => ({ ...current, telephone: event.target.value }))}
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
                    placeholder="Optionnel"
                  />
                </div>
              </div>

              <div className="rounded-2xl border border-gray-100 p-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Photo du membre</label>
                  <input
                    key={imageInputKey}
                    type="file"
                    accept="image/*"
                    onChange={(event) => setSelectedImageFile(event.target.files?.[0] ?? null)}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:px-4 file:py-2.5 file:rounded-xl file:border-0 file:bg-orange-50 file:text-[#E8521A] file:font-medium hover:file:bg-orange-100"
                  />
                  <p className="mt-2 text-xs text-gray-500">
                    La photo est téléversée dans le stockage cloud sécurisé de l&apos;application.
                  </p>
                </div>

                <div className="mt-4 flex items-center gap-4">
                  <img
                    src={imagePreviewUrl || editingMember?.avatar || DEFAULT_AVATAR}
                    alt="Aperçu"
                    className="w-20 h-20 rounded-full object-cover border border-orange-100"
                  />
                  <div className="text-sm text-gray-500">
                    <p>Aperçu de la photo du membre.</p>
                    <p className="mt-1">Si aucune image n&apos;est fournie, un avatar par défaut sera utilisé.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-5 border-t border-gray-100 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={() => void handleSave()}
                className="px-5 py-2.5 rounded-xl bg-[#E8521A] text-white text-sm font-semibold hover:bg-[#d44a16] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? "Enregistrement..." : editingMember ? "Mettre à jour" : "Ajouter"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
